import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
  Stack,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Add as AddIcon,
  FamilyRestroom as FamilyIcon,
  ViewList as SimpleViewIcon,
  AccountTree as InteractiveViewIcon,
  NavigateNext as NavigateNextIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { FamilyTree as FamilyTreeType, TreeViewMode, TreeLayoutAlgorithm, TreeExportFormat, EnhancedFamilyTree, CompleteRelationships } from '../types';
import { TreeRelationshipService } from '../services/treeRelationshipService';
import { TreeExportService } from '../services/treeExportService';
import { PersonService } from '../services/personService';
import { SimpleTreeView } from '../components/tree/SimpleTreeView';
import { InteractiveTreeView } from '../components/tree/InteractiveTreeView';
import { TreeControls } from '../components/tree/TreeControls';

/**
 * FamilyTree Page
 *
 * Main page for viewing family tree hierarchy
 *
 * Features:
 * - Toggle between Simple and Interactive views
 * - Load tree from root or focused person
 * - Expand/collapse branches
 * - Navigate to person profiles
 * - Search for people in tree
 * - Switch view modes (descendant/ancestor)
 * - Interactive features: pan, zoom, export (Interactive view only)
 *
 * @component
 */

type TreeViewType = 'simple' | 'interactive';

const FamilyTree: React.FC = () => {
  const navigate = useNavigate();
  const { personId: urlPersonId } = useParams<{ personId?: string }>();
  const svgRef = useRef<SVGSVGElement>(null);
  const changeSubscriptionRef = useRef<(() => void) | null>(null);

  // ===== State =====
  const [tree, setTree] = useState<FamilyTreeType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Prioritize URL parameter over localStorage
  const [focusPersonId, setFocusPersonId] = useState<string | null>(
    urlPersonId || localStorage.getItem('familyTreeFocusPerson') || null
  );
  const [viewMode, setViewMode] = useState<TreeViewMode>('descendant');
  const [viewType, setViewType] = useState<TreeViewType>('simple');
  const [layoutAlgorithm, setLayoutAlgorithm] = useState<TreeLayoutAlgorithm>('tree');
  const [expandAll, setExpandAll] = useState(false);
  const [collapseAll, setCollapseAll] = useState(false);
  const [zoomLevel, setZoomLevel] = useState<number | undefined>(undefined);
  const [fitToView, setFitToView] = useState<boolean>(false);

  // Navigation history state
  const [navigationHistory, setNavigationHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [breadcrumbPath, setBreadcrumbPath] = useState<{ id: string; name: string }[]>([]);
  const [hasParentAvailable, setHasParentAvailable] = useState(false);

  // ===== Handlers =====

  /**
   * Load tree data using enhanced TreeRelationshipService
   */
  const loadTree = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let personId = focusPersonId;

      // PRIORITY 1: URL parameter (new behavior)
      if (urlPersonId) {
        personId = urlPersonId;
        setFocusPersonId(urlPersonId);
        localStorage.setItem('familyTreeFocusPerson', urlPersonId);
      }
      // PRIORITY 2: State/localStorage
      else if (!personId) {
        // PRIORITY 3: Find oldest ancestor as fallback
        const oldestAncestor = await PersonService.findOldestAncestor();
        if (!oldestAncestor) {
          // No people in database
          setTree(null);
          setLoading(false);
          return;
        }
        personId = oldestAncestor.id;
        setFocusPersonId(personId);
      }

      // Get TreeRelationshipService instance
      const relationshipService = TreeRelationshipService.getInstance();

      // Enable change detection for real-time updates (only once per component mount)
      if (viewType === 'interactive' && !changeSubscriptionRef.current) {
        relationshipService.enableChangeDetection();

        // Subscribe to change events for auto-refresh
        const unsubscribe = relationshipService.watchChanges((event) => {
          console.log('FamilyTree: Tree data changed, invalidating cache...', event);
          // Just invalidate cache, don't reload to prevent infinite loop
          // The next user interaction will load fresh data
        });

        // Store unsubscribe function for cleanup
        changeSubscriptionRef.current = unsubscribe;
      }

      // Build enhanced tree with all relationship types
      const enhancedTree = await relationshipService.buildEnhancedTree({
        rootPersonId: personId,
        viewMode,
        maxDepth: 15,
        includeSiblings: viewType === 'interactive', // Only include siblings in interactive view
        includeNonBiological: true,
        forceRefresh: false, // Use cache when available
      });

      // EnhancedFamilyTree is already compatible with FamilyTreeType
      // (it extends FamilyTree)
      setTree(enhancedTree);
    } catch (error: any) {
      console.error('Failed to load tree:', error);
      setError(error.message || 'Failed to load family tree. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [urlPersonId, focusPersonId, viewMode, viewType]);

  /**
   * Handle person click - focus tree on that person
   * Adds to navigation history
   */
  const handlePersonClick = (personId: string) => {
    // Don't add to history if it's the same person
    if (personId === focusPersonId) return;

    // Add to history stack
    const newHistory = navigationHistory.slice(0, historyIndex + 1);
    newHistory.push(personId);
    setNavigationHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);

    // Save to localStorage
    try {
      localStorage.setItem('familyTreeNavigationHistory', JSON.stringify(newHistory));
      localStorage.setItem('familyTreeHistoryIndex', String(newHistory.length - 1));
    } catch (error) {
      console.warn('Failed to save navigation history to localStorage', error);
    }

    setFocusPersonId(personId);
  };

  /**
   * Handle focus person change
   */
  const handleFocusPerson = (personId: string) => {
    handlePersonClick(personId);
  };

  /**
   * Navigate back in history
   */
  const handleNavigateBack = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setFocusPersonId(navigationHistory[newIndex]);

      // Save to localStorage
      try {
        localStorage.setItem('familyTreeHistoryIndex', String(newIndex));
      } catch (error) {
        console.warn('Failed to save history index to localStorage', error);
      }
    }
  };

  /**
   * Navigate forward in history
   */
  const handleNavigateForward = () => {
    if (historyIndex < navigationHistory.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setFocusPersonId(navigationHistory[newIndex]);

      // Save to localStorage
      try {
        localStorage.setItem('familyTreeHistoryIndex', String(newIndex));
      } catch (error) {
        console.warn('Failed to save history index to localStorage', error);
      }
    }
  };

  /**
   * Navigate to parent(s) of current person
   * Shows both parents when available
   * NOTE: Keyboard shortcut (Alt+Up) could be implemented for parent navigation
   */
  const handleNavigateToParent = async () => {
    if (!focusPersonId) return;

    try {
      const relationshipService = TreeRelationshipService.getInstance();
      const relationships = await relationshipService.getAllRelationships(focusPersonId);

      // Priority: biological parents > adoptive > step parents
      let parents = relationships.parents;
      if (parents.length === 0) {
        parents = relationships.adoptiveParents;
      }
      if (parents.length === 0) {
        parents = relationships.stepParents;
      }

      if (parents.length > 0) {
        // Navigate to first parent (usually father based on sort order)
        handlePersonClick(parents[0].person.id);
      }
    } catch (error) {
      console.error('Failed to navigate to parent:', error);
      setError('Failed to navigate to parent');
    }
  };

  /**
   * Navigate to root ancestor
   */
  const handleNavigateToRoot = async () => {
    try {
      const oldestAncestor = await PersonService.findOldestAncestor();
      if (oldestAncestor) {
        handlePersonClick(oldestAncestor.id);
      }
    } catch (error) {
      console.error('Failed to navigate to root:', error);
      setError('Failed to navigate to root');
    }
  };

  /**
   * Handle view mode change
   */
  const handleViewModeChange = (mode: TreeViewMode) => {
    setViewMode(mode);
  };

  /**
   * Navigate to add person page
   */
  const handleAddPerson = () => {
    navigate('/people/add');
  };

  /**
   * Handle expand all nodes
   */
  const handleExpandAll = () => {
    setExpandAll(true);
    setCollapseAll(false);
  };

  /**
   * Handle collapse all nodes
   */
  const handleCollapseAll = () => {
    setCollapseAll(true);
    setExpandAll(false);
  };

  /**
   * Reset expand/collapse triggers after completion
   */
  const handleExpandCollapseComplete = () => {
    setExpandAll(false);
    setCollapseAll(false);
  };

  /**
   * Handle view type toggle (simple vs interactive)
   */
  const handleViewTypeChange = (_event: React.MouseEvent<HTMLElement>, newViewType: TreeViewType | null) => {
    if (newViewType !== null) {
      setViewType(newViewType);
      // Save preference to localStorage
      localStorage.setItem('familyTreeViewType', newViewType);
    }
  };

  /**
   * Handle zoom in
   */
  const handleZoomIn = () => {
    setZoomLevel((prev) => (prev || 1) * 1.2);
  };

  /**
   * Handle zoom out
   */
  const handleZoomOut = () => {
    setZoomLevel((prev) => (prev || 1) / 1.2);
  };

  /**
   * Handle fit to view
   */
  const handleFitToView = () => {
    setFitToView(true);
    setTimeout(() => setFitToView(false), 100);
  };

  /**
   * Handle tree export
   */
  const handleExport = async (format: TreeExportFormat) => {
    try {
      // Find the SVG element in the InteractiveTreeView
      const svg = document.querySelector('.interactive-tree-view svg') as SVGSVGElement;
      if (!svg) {
        throw new Error('Could not find tree SVG element');
      }

      await TreeExportService.exportTree(svg, {
        format,
        fileName: `family-tree-${new Date().toISOString().split('T')[0]}`,
      });
    } catch (error: any) {
      console.error('Failed to export tree:', error);
      setError(error.message || 'Failed to export tree');
    }
  };

  /**
   * Handle layout algorithm change
   */
  const handleLayoutAlgorithmChange = (algorithm: TreeLayoutAlgorithm) => {
    setLayoutAlgorithm(algorithm);
    localStorage.setItem('familyTreeLayoutAlgorithm', algorithm);
  };

  // ===== Utility Functions =====

  /**
   * Calculate breadcrumb path from root ancestor to current person
   * Max 5 levels with ellipsis for deep trees
   */
  const calculateBreadcrumbPath = useCallback(async (personId: string) => {
    if (!personId) return;

    try {
      const relationshipService = TreeRelationshipService.getInstance();
      const path: { id: string; name: string }[] = [];
      let currentId: string | null = personId;
      const maxLevels = 10; // Collect more than needed for truncation logic
      let iterations = 0;

      // Walk up the tree to find ancestors
      while (currentId && iterations < maxLevels) {
        const person = await PersonService.getPersonById(currentId);
        if (!person) break;

        path.unshift({
          id: person.id,
          name: `${person.firstName} ${person.lastName}`,
        });

        // Get parent
        const rels: CompleteRelationships = await relationshipService.getAllRelationships(currentId);
        let parents = rels.parents;
        if (parents.length === 0) {
          parents = rels.adoptiveParents;
        }
        if (parents.length === 0) {
          parents = rels.stepParents;
        }

        if (parents.length === 0) break; // Reached root
        currentId = parents[0].person.id; // Follow primary parent
        iterations++;
      }

      // Truncate to max 5 levels
      if (path.length > 5) {
        // Keep first, last 3, and add ellipsis
        const truncated = [
          path[0],
          { id: '...', name: '...' },
          ...path.slice(-3),
        ];
        setBreadcrumbPath(truncated);
      } else {
        setBreadcrumbPath(path);
      }
    } catch (error) {
      console.error('Failed to calculate breadcrumb path:', error);
    }
  }, []);

  /**
   * Check if current person is a leaf node (no descendants)
   */
  const isLeafNode = (): boolean => {
    return tree !== null && tree.root.children.length === 0;
  };

  /**
   * Check if current person has parents
   */
  const hasParent = async (): Promise<boolean> => {
    if (!focusPersonId) return false;

    try {
      const relationshipService = TreeRelationshipService.getInstance();
      const relationships = await relationshipService.getAllRelationships(focusPersonId);
      return relationships.parents.length > 0 ||
        relationships.adoptiveParents.length > 0 ||
        relationships.stepParents.length > 0;
    } catch (error) {
      return false;
    }
  };

  // ===== Effects =====

  /**
   * Load tree on mount or when focus/view changes
   */
  useEffect(() => {
    loadTree();
  }, [loadTree]);

  /**
   * Cleanup change detection subscription on unmount
   */
  useEffect(() => {
    return () => {
      if (changeSubscriptionRef.current) {
        changeSubscriptionRef.current();
        changeSubscriptionRef.current = null;
      }
    };
  }, []);

  /**
   * Load saved preferences and navigation history
   */
  useEffect(() => {
    const savedViewType = localStorage.getItem('familyTreeViewType') as TreeViewType;
    if (savedViewType === 'simple' || savedViewType === 'interactive') {
      setViewType(savedViewType);
    }

    const savedAlgorithm = localStorage.getItem('familyTreeLayoutAlgorithm') as TreeLayoutAlgorithm;
    if (savedAlgorithm) {
      setLayoutAlgorithm(savedAlgorithm);
    }

    // Load navigation history from localStorage
    try {
      const savedHistory = localStorage.getItem('familyTreeNavigationHistory');
      const savedIndex = localStorage.getItem('familyTreeHistoryIndex');

      if (savedHistory && savedIndex) {
        const history = JSON.parse(savedHistory) as string[];
        const index = parseInt(savedIndex, 10);

        if (Array.isArray(history) && history.length > 0 && index >= 0 && index < history.length) {
          setNavigationHistory(history);
          setHistoryIndex(index);
          // Don't auto-set focusPersonId here, let loadTree handle initial person
        }
      }
    } catch (error) {
      console.warn('Failed to load navigation history from localStorage', error);
    }
  }, []);

  /**
   * Calculate breadcrumb path when focus person changes
   */
  useEffect(() => {
    if (focusPersonId) {
      calculateBreadcrumbPath(focusPersonId);
    }
  }, [focusPersonId, calculateBreadcrumbPath]);

  /**
   * Check parent availability when focus person changes
   */
  useEffect(() => {
    const checkParent = async () => {
      const parentExists = await hasParent();
      setHasParentAvailable(parentExists);
    };

    if (focusPersonId) {
      checkParent();
    }
  }, [focusPersonId]);

  // ===== Render =====

  // Loading State
  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            gap: 3,
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">
            Building family tree...
          </Typography>
        </Box>
      </Container>
    );
  }

  // Empty State - No people in database
  if (!tree) {
    return (
      <Container maxWidth="md">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            gap: 3,
            textAlign: 'center',
          }}
        >
          <FamilyIcon sx={{ fontSize: 80, color: 'text.disabled' }} />
          <Typography variant="h4" gutterBottom>
            No Family Tree Yet
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Start building your family tree by adding people and their relationships.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={handleAddPerson}
          >
            Add Your First Family Member
          </Button>
        </Box>
      </Container>
    );
  }

  // Error State
  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <Alert
            severity="error"
            onClose={() => setError(null)}
            action={
              <Button color="inherit" size="small" onClick={loadTree}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        </Box>
      </Container>
    );
  }

  // Main Tree View
  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Page Header */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Box>
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <FamilyIcon fontSize="large" color="primary" />
                Family Tree
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Explore your family relationships across generations
              </Typography>
            </Box>

            {/* View Type Toggle */}
            <ToggleButtonGroup
              value={viewType}
              exclusive
              onChange={handleViewTypeChange}
              size="small"
              sx={{ height: 'fit-content' }}
            >
              <ToggleButton value="simple" aria-label="simple view">
                <SimpleViewIcon sx={{ mr: 1 }} />
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  Simple
                </Box>
              </ToggleButton>
              <ToggleButton value="interactive" aria-label="interactive view">
                <InteractiveViewIcon sx={{ mr: 1 }} />
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  Interactive
                </Box>
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          <Divider />
        </Box>

        {/* Breadcrumb Navigation */}
        {breadcrumbPath.length > 0 && (
          <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
            <Breadcrumbs
              separator={<NavigateNextIcon fontSize="small" />}
              aria-label="family tree breadcrumb"
            >
              {breadcrumbPath.map((crumb, index) => {
                const isLast = index === breadcrumbPath.length - 1;
                const isEllipsis = crumb.id === '...';

                if (isEllipsis) {
                  return (
                    <Typography key="ellipsis" color="text.secondary">
                      ...
                    </Typography>
                  );
                }

                if (isLast) {
                  return (
                    <Typography key={crumb.id} color="text.primary" fontWeight="bold">
                      {crumb.name}
                    </Typography>
                  );
                }

                return (
                  <Link
                    key={crumb.id}
                    component="button"
                    variant="body2"
                    onClick={() => handlePersonClick(crumb.id)}
                    sx={{
                      cursor: 'pointer',
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    {crumb.name}
                  </Link>
                );
              })}
            </Breadcrumbs>
          </Paper>
        )}

        {/* Leaf Node Suggestion */}
        {viewMode === 'descendant' && isLeafNode() && hasParentAvailable && (
          <Alert
            severity="info"
            icon={<InfoIcon />}
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={handleNavigateToParent}>
                View Parents
              </Button>
            }
          >
            This person has no recorded descendants. Would you like to view their ancestors?
          </Alert>
        )}

        {/* Tree Controls */}
        <TreeControls
          onFocusPerson={handleFocusPerson}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          currentPersonId={focusPersonId || undefined}
          totalPersons={tree.totalPersons}
          totalGenerations={tree.totalGenerations}
          onExpandAll={handleExpandAll}
          onCollapseAll={handleCollapseAll}
          onZoomIn={viewType === 'interactive' ? handleZoomIn : undefined}
          onZoomOut={viewType === 'interactive' ? handleZoomOut : undefined}
          onFitToView={viewType === 'interactive' ? handleFitToView : undefined}
          onExport={viewType === 'interactive' ? handleExport : undefined}
          layoutAlgorithm={layoutAlgorithm}
          onLayoutAlgorithmChange={viewType === 'interactive' ? handleLayoutAlgorithmChange : undefined}
          isInteractiveView={viewType === 'interactive'}
          onNavigateBack={handleNavigateBack}
          onNavigateForward={handleNavigateForward}
          onNavigateToParent={handleNavigateToParent}
          onNavigateToRoot={handleNavigateToRoot}
          canNavigateBack={historyIndex > 0}
          canNavigateForward={historyIndex < navigationHistory.length - 1}
          hasParent={hasParentAvailable}
          navigationPosition={
            navigationHistory.length > 0
              ? `${historyIndex + 1} of ${navigationHistory.length}`
              : undefined
          }
        />

        {/* Error Alert (if any) */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Tree Visualization */}
        {viewType === 'simple' ? (
          <Paper
            elevation={2}
            sx={{
              borderRadius: 2,
              overflow: 'auto',
              bgcolor: 'background.paper',
            }}
          >
            <SimpleTreeView
              root={tree.root}
              onPersonClick={handlePersonClick}
              viewMode={viewMode}
              expandAll={expandAll}
              collapseAll={collapseAll}
              onExpandCollapseComplete={handleExpandCollapseComplete}
            />
          </Paper>
        ) : (
          <Paper
            elevation={2}
            className="interactive-tree-view"
            sx={{
              width: '100%',
              height: '800px',
              minHeight: '600px',
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: 'background.paper',
            }}
          >
            <InteractiveTreeView
              tree={tree}
              viewMode={viewMode}
              layoutAlgorithm={layoutAlgorithm}
              onPersonClick={handlePersonClick}
              enableCollapse={true}
              zoomLevel={zoomLevel}
              fitToView={fitToView}
            />
          </Paper>
        )}

        {/* Help Text */}
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            {viewType === 'simple'
              ? 'Click on any person to view their full profile. Use the controls above to focus on a specific person or change the view mode.'
              : 'Click to select, double-click to expand/collapse, drag to pan, scroll to zoom. Use the controls above for additional features.'}
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

export default FamilyTree;
